import i18n from '@/util/config/i18n'
import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { APP_LINK_SCHEME } from '@/util/link-href'

/**
 * Opens an http(s) page in a new tab, and returns false when the href is not http(s), the browser blocked the pop-up,
 * or window.open rejects the URL.
 *
 * `noopener` would make window.open return null, so a blocked pop-up could not be told from a success. Instead the
 * opener is cut by hand, which has the same effect.
 */
export function openInNewTab(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false

  let tab: Window | null
  try {
    tab = window.open(href, '_blank')
  } catch {
    return false
  }
  if (!tab) return false
  tab.opener = null
  return true
}

/**
 * Opens the page and tells the user how it went: a short confirmation, or a "blocked" warning. A mailto:, tel: or sms:
 * link goes to the system's mail, phone or messaging app. It is opened in a new browsing context, as before, so it never
 * navigates this page (and trips the unsaved-changes prompt); no tab is left to report on, so it gets no snackbar.
 */
export function openInNewTabWithFeedback(href: string): boolean {
  if (APP_LINK_SCHEME.test(href)) {
    window.open(href, '_blank')
    return true
  }
  const opened = openInNewTab(href)
  useSnackbarStore().enqueue({
    label: i18n.t(opened ? 'linkMenu.openedInNewTab' : 'linkMenu.openBlocked') as string,
    leadingIcon: true,
    variant: opened ? 'success' : 'warning'
  })
  return opened
}
